import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  keyframes,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Slide,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { useWebSocket, useUser } from './WebSocketContext';
import { useNavigate, useLocation, matchPath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '../styles/Sidebar.css';

// Animation keyframes for the floating effect
const floatAnimation = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-3px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const normalizeServerMessageType = (value) => {
  const normalizedValue = String(value || '').toLowerCase();
  if (['error', 'failed', 'tool_failed', 'agent_failed', 'timeout'].includes(normalizedValue)) {
    return 'error';
  }
  if (['warning', 'agent_finished', 'step_blocked', 'planner_warning', 'max_iterations'].includes(normalizedValue)) {
    return 'warning';
  }
  if (['success', 'goal_reached', 'tool_succeeded', 'completed', 'verified'].includes(normalizedValue)) {
    return 'success';
  }
  return 'info';
};

const normalizeServerMessagePayload = (payload) => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const message = typeof payload.message === 'string'
      ? payload.message
      : JSON.stringify(payload);
    const type = normalizeServerMessageType(
      payload.type || payload.status || payload.event_type
    );
    const dedupeKey = String(
      payload.event_key || payload.id || `${type}:${message}`
    );
    return {
      id: `${dedupeKey}:${Date.now()}`,
      dedupeKey,
      message,
      type,
      timestamp: new Date()
    };
  }

  const message = String(payload ?? '').trim();
  if (!message) {
    return null;
  }

  return {
    id: `${Date.now()}:${message}`,
    dedupeKey: `legacy:${message}`,
    message,
    type: 'info',
    timestamp: new Date()
  };
};

const normalizeNotificationTimestamp = (value) => {
  if (value instanceof Date) {
    const timestamp = value.getTime();
    return Number.isFinite(timestamp) ? timestamp : Date.now();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value > 1e12 ? value : value * 1000;
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return Date.now();
    }

    const numericValue = Number(trimmedValue);
    if (Number.isFinite(numericValue)) {
      return numericValue > 1e12 ? numericValue : numericValue * 1000;
    }

    const parsedValue = Date.parse(trimmedValue);
    if (Number.isFinite(parsedValue)) {
      return parsedValue;
    }
  }

  return Date.now();
};

const formatNotificationTimestamp = (timestamp, locale) => {
  const date = new Date(normalizeNotificationTimestamp(timestamp));

  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(date);
  } catch (error) {
    return date.toLocaleString();
  }
};

const normalizePositiveCount = (value) => {
  const count = Number.parseInt(value, 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
};

const normalizeNonNegativeCount = (value) => {
  const count = Number.parseInt(value, 10);
  return Number.isFinite(count) && count >= 0 ? count : null;
};

const BADGE_MAX_COUNT = 9999;

const formatCompactPath = (value) => {
  const path = String(value || '').trim();
  if (!path) {
    return '';
  }

  const parts = path.split(/[/\\]+/).filter(Boolean);
  if (parts.length > 3) {
    return `.../${parts.slice(-3).join('/')}`;
  }

  return path;
};

const getCaseIdFromPathname = (pathname) => {
  const patterns = [
    '/case/:caseid',
    '/case/:caseid/*',
    '/public/case/:caseid',
    '/public/case/:caseid/*',
  ];

  for (const pattern of patterns) {
    const match = matchPath(pattern, pathname);
    if (!match) {
      continue;
    }

    const parsedCaseId = Number.parseInt(match.params.caseid, 10);
    if (Number.isInteger(parsedCaseId) && parsedCaseId > 0) {
      return parsedCaseId;
    }
  }

  return null;
};

const StatusBar = () => {
  const { subscribeToMessages, sendMessage, isConnected } = useWebSocket();
  const { canManageProject, isLoggedIn } = useUser();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [filesCount, setFilesCount] = useState(0);
  const [activeTasksInfo, setActiveTasksInfo] = useState(null);
  const [postProcessingQueueCount, setPostProcessingQueueCount] = useState(0);
  const [postProcessingTotalQueueCount, setPostProcessingTotalQueueCount] = useState(0);
  const [postProcessingCurrentItem, setPostProcessingCurrentItem] = useState(null);
  const [postProcessingCurrentWidgetItem, setPostProcessingCurrentWidgetItem] = useState(null);
  const [postProcessingWaitingCaseIds, setPostProcessingWaitingCaseIds] = useState([]);
  const [postProcessingWaitingCountsByCase, setPostProcessingWaitingCountsByCase] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState(null);
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const [serverMessages, setServerMessages] = useState([]);
  const [pendingClearQueueCaseId, setPendingClearQueueCaseId] = useState(null);
  const [fileProcessingActionsAnchorEl, setFileProcessingActionsAnchorEl] = useState(null);
  const [postProcessingActionsAnchorEl, setPostProcessingActionsAnchorEl] = useState(null);
  const primaryColor = '#5757b0';
  const lightPurple = '#f0f0ff';
  const navigate = useNavigate();
  const currentCaseId = getCaseIdFromPathname(location.pathname);
  const isClearQueuePending = pendingClearQueueCaseId !== null && pendingClearQueueCaseId === currentCaseId;
  const fileProcessingDetails = activeTasksInfo && typeof activeTasksInfo === 'object' && !Array.isArray(activeTasksInfo)
    ? activeTasksInfo
    : null;
  const fileProcessingActiveCount = fileProcessingDetails && !fileProcessingDetails.error
    ? normalizePositiveCount(fileProcessingDetails.files_in_progress_count)
    : normalizePositiveCount(filesCount);
  const fileProcessingItems = Array.isArray(fileProcessingDetails?.files_in_progress)
    ? fileProcessingDetails.files_in_progress
    : [];
  const fileProcessingQueueSize = normalizePositiveCount(fileProcessingDetails?.queue_size);
  const fileProcessingBadgeCount = fileProcessingActiveCount + fileProcessingQueueSize;
  const fileProcessingActiveTasksCount = normalizePositiveCount(fileProcessingDetails?.active_tasks_count);
  const fileProcessingMaxWorkers = normalizePositiveCount(fileProcessingDetails?.max_workers);
  const fileProcessingProgressReturned = normalizePositiveCount(fileProcessingDetails?.progress_files_returned);
  const fileProcessingProgressTotal = normalizePositiveCount(fileProcessingDetails?.progress_files_total);
  const fileProcessingIsTruncated = Boolean(fileProcessingDetails?.is_progress_truncated);
  const isFileProcessingActionsOpen = Boolean(fileProcessingActionsAnchorEl);
  const currentCasePostProcessingWaitingCount = currentCaseId
    ? Number.parseInt(
        postProcessingWaitingCountsByCase[currentCaseId]
        ?? (postProcessingWaitingCaseIds.includes(currentCaseId) ? 1 : 0),
        10
      )
    : 0;
  const postProcessingGlobalActiveCount = [
    postProcessingCurrentItem,
    postProcessingCurrentWidgetItem,
  ].filter((item) => item && typeof item === 'object').length;
  const currentCasePostProcessingActiveCount = [
    postProcessingCurrentItem,
    postProcessingCurrentWidgetItem,
  ].filter((item) => {
    if (!item || typeof item !== 'object') {
      return false;
    }

    const itemCaseId = Number.parseInt(item.case_id ?? item.caseId ?? 0, 10);
    return Number.isInteger(itemCaseId) && itemCaseId === currentCaseId;
  }).length;
  const activeActionItems = [postProcessingCurrentItem]
    .filter((item) => item?.type === 'action_force_run');
  const activeActionTimeeventTotalCount = activeActionItems
    .reduce((total, item) => total + normalizePositiveCount(item.timeevent_count), 0);
  const activeActionHasRemainingCount = activeActionItems
    .some((item) => normalizeNonNegativeCount(item.remaining_timeevent_count) !== null);
  const activeActionTimeeventDisplayCount = activeActionItems
    .reduce((total, item) => {
      const remainingCount = normalizeNonNegativeCount(item.remaining_timeevent_count);
      return total + (remainingCount !== null ? remainingCount : normalizePositiveCount(item.timeevent_count));
    }, 0);
  const postProcessingVisibleTotalCount = postProcessingQueueCount + postProcessingGlobalActiveCount;
  const hasVisiblePostProcessingWork = postProcessingVisibleTotalCount > 0;
  const isPostProcessingVisible = hasVisiblePostProcessingWork || postProcessingTotalQueueCount > 0;
  const isPostProcessingAnimating = hasVisiblePostProcessingWork;
  const hasWaitingPostProcessingQueue = Number.isFinite(currentCasePostProcessingWaitingCount)
    && currentCasePostProcessingWaitingCount > 0;
  const canClearPostProcessingQueue = hasWaitingPostProcessingQueue && canManageProject;
  const isPostProcessingActionsOpen = Boolean(postProcessingActionsAnchorEl);
  const visibleBadgeCount = [
    fileProcessingBadgeCount > 0,
    isPostProcessingVisible,
    notifications.length > 0,
  ].filter(Boolean).length;
  const statusBarBadgeRowWidth = visibleBadgeCount > 0
    ? (visibleBadgeCount * 34) + ((visibleBadgeCount - 1) * 4) + 8
    : 0;
  const postProcessingSummaryContent = (
    <Stack spacing={0.25} sx={{ maxWidth: 320 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {t(
          'status_bar.postprocessing_tooltip_current_project_waiting',
          'Current project waiting: {{count}} job(s).',
          { count: currentCasePostProcessingWaitingCount }
        )}
      </Typography>
      <Typography variant="body2">
        {t(
          'status_bar.postprocessing_tooltip_current_project_active',
          'Current project active now: {{count}} job(s).',
          { count: currentCasePostProcessingActiveCount }
        )}
      </Typography>
      <Typography variant="body2">
        {t(
          'status_bar.postprocessing_tooltip_global_waiting',
          'All projects waiting: {{count}} job(s).',
          { count: postProcessingQueueCount }
        )}
      </Typography>
      <Typography variant="body2">
        {t(
          'status_bar.postprocessing_tooltip_global_active',
          'All projects active now: {{count}} job(s).',
          { count: postProcessingGlobalActiveCount }
        )}
      </Typography>
      <Typography variant="body2">
        {t(
          'status_bar.postprocessing_tooltip_visible_total',
          'Visible post-processing work: {{count}} job(s) total.',
          { count: postProcessingVisibleTotalCount }
        )}
      </Typography>
      <Typography variant="body2" sx={{ opacity: 0.85 }}>
        {t(
          'status_bar.postprocessing_tooltip_service_total',
          'Persistent queue total (waiting + active): {{count}} job(s).',
          { count: postProcessingTotalQueueCount }
        )}
      </Typography>
      {activeActionTimeeventTotalCount > 0 && (
        <Typography variant="body2" sx={{ color: primaryColor, fontWeight: 700 }}>
          {activeActionHasRemainingCount
            ? t(
                'status_bar.postprocessing_tooltip_active_action_events_remaining',
                'Events remaining in the active action job: {{count}} of {{total}}.',
                { count: activeActionTimeeventDisplayCount, total: activeActionTimeeventTotalCount }
              )
            : t(
                'status_bar.postprocessing_tooltip_active_action_events',
                'Events in the active action job: {{count}}.',
                { count: activeActionTimeeventDisplayCount }
              )}
        </Typography>
      )}
    </Stack>
  );
  const MAX_NOTIFICATIONS = 6;
  const MAX_SERVER_MESSAGES = 3;
  const isNotificationsOpen = Boolean(notificationsAnchorEl);
  const notificationLocale = i18n.resolvedLanguage || i18n.language || undefined;
  const fileProcessingSummaryContent = (
    <Stack spacing={0.25} sx={{ maxWidth: 340 }}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        {t(
          'status_bar.file_processing_tooltip_current',
          '{{count}} event(s) waiting or being processed.',
          { count: fileProcessingBadgeCount }
        )}
      </Typography>
      {canManageProject ? (
        isFileProcessingActionsOpen && activeTasksInfo === null ? (
          <Typography variant="body2">
            {t(
              'status_bar.file_processing_tooltip_loading',
              'Click to load detailed event processing information.'
            )}
          </Typography>
        ) : fileProcessingDetails?.error ? (
          <Typography variant="body2">
            {t(
              'status_bar.file_processing_tooltip_error',
              'Detailed event processing information is unavailable right now.'
            )}
          </Typography>
        ) : (
          <>
            <Typography variant="body2">
              {t(
                'status_bar.file_processing_tooltip_queue',
                'Queued events: {{count}}.',
                { count: fileProcessingQueueSize }
              )}
            </Typography>
            <Typography variant="body2">
              {t(
                'status_bar.file_processing_tooltip_active_tasks',
                'Active tasks: {{count}}.',
                { count: fileProcessingActiveTasksCount }
              )}
            </Typography>
            <Typography variant="body2">
              {t(
                'status_bar.file_processing_tooltip_workers',
                'Worker pool: {{count}}.',
                { count: fileProcessingMaxWorkers }
              )}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85 }}>
              {t(
                fileProcessingIsTruncated
                  ? 'status_bar.file_processing_tooltip_loaded_truncated'
                  : 'status_bar.file_processing_tooltip_loaded',
                fileProcessingIsTruncated
                  ? 'Showing {{shown}} of {{total}} detailed event(s).'
                  : 'Loaded details: {{count}} event(s).',
                fileProcessingIsTruncated
                  ? { shown: fileProcessingProgressReturned, total: fileProcessingProgressTotal || fileProcessingProgressReturned }
                  : { count: fileProcessingItems.length }
              )}
            </Typography>
          </>
        )
      ) : (
        <Typography variant="body2" sx={{ opacity: 0.85 }}>
          {t(
            'status_bar.file_processing_tooltip_restricted',
            'Detailed event processing information is available to project managers only.'
          )}
        </Typography>
      )}
    </Stack>
  );

  useEffect(() => {
    setPendingClearQueueCaseId(null);
    setPostProcessingActionsAnchorEl(null);
  }, [currentCaseId]);

  useEffect(() => {
    if (!isConnected) {
      setPendingClearQueueCaseId(null);
    }
  }, [isConnected]);

  useEffect(() => {
    if (!canClearPostProcessingQueue) {
      setPostProcessingActionsAnchorEl(null);
    }
  }, [canClearPostProcessingQueue]);

  useEffect(() => {
    if (!isLoggedIn) {
      return undefined;
    }

    sendMessage({
      command: 'get_postprocessing_status',
      data: {},
    });
  }, [isLoggedIn, sendMessage]);

  useEffect(() => {
    const unsubscribe = subscribeToMessages((parsedMessage) => {
      try {
        // Message is already parsed in WebSocketContext
        const message = parsedMessage;
        if (message.command === 'file_dequeued') {
          // Use total_count (queued + processing) if available, otherwise fallback to legacy count
          const count = Number(message.data?.total_count ?? message.data?.count ?? 0);
          setFilesCount(Number.isFinite(count) ? count : 0);
        }

        if (message.command === 'active_tasks_info') {
          const tasksInfo = message.data && typeof message.data === 'object' && !Array.isArray(message.data)
            ? message.data
            : null;
          setActiveTasksInfo(tasksInfo);
        }

        if (message.command === 'postprocessing_status') {
          const totalQueueCount = Number.parseInt(
            message.data?.queue_size ?? message.data?.waiting_queue_size ?? 0,
            10
          );
          const waitingQueueCount = Number.parseInt(
            message.data?.waiting_queue_size ?? message.data?.queue_size ?? 0,
            10
          );
          setPostProcessingTotalQueueCount(
            Number.isFinite(totalQueueCount) && totalQueueCount > 0 ? totalQueueCount : 0
          );
          setPostProcessingQueueCount(
            Number.isFinite(waitingQueueCount) && waitingQueueCount > 0 ? waitingQueueCount : 0
          );
          const waitingCaseIds = Array.isArray(message.data?.waiting_case_ids)
            ? message.data.waiting_case_ids
                .map((caseId) => Number.parseInt(caseId, 10))
                .filter((caseId) => Number.isInteger(caseId) && caseId > 0)
            : [];
          setPostProcessingWaitingCaseIds(waitingCaseIds);
          const waitingCountsByCase = {};
          const rawWaitingCountsByCase = message.data?.waiting_counts_by_case;
          if (rawWaitingCountsByCase && typeof rawWaitingCountsByCase === 'object' && !Array.isArray(rawWaitingCountsByCase)) {
            Object.entries(rawWaitingCountsByCase).forEach(([caseId, count]) => {
              const parsedCaseId = Number.parseInt(caseId, 10);
              const parsedCount = Number.parseInt(count, 10);
              if (Number.isInteger(parsedCaseId) && parsedCaseId > 0 && Number.isFinite(parsedCount) && parsedCount > 0) {
                waitingCountsByCase[parsedCaseId] = parsedCount;
              }
            });
          }
          if (Object.keys(waitingCountsByCase).length === 0 && waitingCaseIds.length > 0) {
            waitingCaseIds.forEach((caseId) => {
              waitingCountsByCase[caseId] = 1;
            });
          }
          setPostProcessingWaitingCountsByCase(waitingCountsByCase);
          const currentItem = message.data?.current_item;
          setPostProcessingCurrentItem(
            currentItem && typeof currentItem === 'object' && !Array.isArray(currentItem)
              ? currentItem
              : null
          );
          const currentWidgetItem = message.data?.current_widget_item;
          setPostProcessingCurrentWidgetItem(
            currentWidgetItem && typeof currentWidgetItem === 'object' && !Array.isArray(currentWidgetItem)
              ? currentWidgetItem
              : null
          );
        }

        if (message.command === "notifications") {
          setNotifications(prev => {
            const notificationData = (
              message.data && typeof message.data === 'object' && !Array.isArray(message.data)
            ) ? message.data : {};
            const timestamp = normalizeNotificationTimestamp(
              notificationData.timestamp
              ?? notificationData.created_at
              ?? notificationData.createdAt
              ?? notificationData.updated_at
              ?? notificationData.updatedAt
            );
            const newNotification = {
              id: `${notificationData.id}:${notificationData.filename}:${Date.now()}`,
              dedupeKey: `${notificationData.id}:${notificationData.filename}:${notificationData.type || ''}`,
              caseid: notificationData.id,
              title: notificationData.title,
              filename: notificationData.filename,
              timestamp
            };

            if (!newNotification.caseid || !newNotification.title || !newNotification.filename) {
              return prev;
            }

            const updatedNotifications = [
              newNotification,
              ...prev.filter(existingNotification => (
                existingNotification.dedupeKey !== newNotification.dedupeKey
              ))
            ];

            return updatedNotifications.slice(0, MAX_NOTIFICATIONS);
          });
          setHasUnreadNotifications(true);
        }

        if (message.command === "notificationMessage") {
          const normalizedServerMessage = normalizeServerMessagePayload(message.data);
          if (!normalizedServerMessage) {
            return;
          }

          setServerMessages(prev => {
            const filteredMessages = prev.filter(
              existingMessage => existingMessage.dedupeKey !== normalizedServerMessage.dedupeKey
            );
            const updatedMessages = [normalizedServerMessage, ...filteredMessages];
            if (updatedMessages.length > MAX_SERVER_MESSAGES) {
              return updatedMessages.slice(0, MAX_SERVER_MESSAGES);
            }

            return updatedMessages;
          });
        }

        if (message.command === "clearProjectQueue") {
          const responseCaseId = Number.parseInt(message.data?.caseid ?? message.data?.case_id, 10);
          if (!Number.isInteger(responseCaseId) || responseCaseId !== currentCaseId) {
            return;
          }

          const responseStatus = String(message.data?.status ?? '').toLowerCase();
          const removedCount = Number.parseInt(message.data?.removed_count ?? 0, 10);
          setPendingClearQueueCaseId((pendingCaseId) => (
            pendingCaseId === responseCaseId ? null : pendingCaseId
          ));
          setPostProcessingActionsAnchorEl(null);
          if (responseStatus !== 'error') {
            setPostProcessingWaitingCaseIds(prev => prev.filter(caseId => caseId !== responseCaseId));
            setPostProcessingWaitingCountsByCase(prev => {
              if (!prev || typeof prev !== 'object') {
                return prev;
              }

              if (!(responseCaseId in prev)) {
                return prev;
              }

              const nextCounts = { ...prev };
              delete nextCounts[responseCaseId];
              return nextCounts;
            });
          }

          const normalizedServerMessage = normalizeServerMessagePayload({
            message: responseStatus === 'error'
              ? t('status_bar.clear_queue_error', 'Unable to clear the queue for this project')
              : (
                  Number.isInteger(removedCount) && removedCount > 0
                    ? t(
                        'status_bar.clear_queue_success',
                        '{{count}} queued item(s) cleared for this project',
                        { count: removedCount }
                      )
                    : t(
                        'status_bar.clear_queue_empty',
                        'No queued items were waiting for this project'
                      )
                ),
            type: responseStatus === 'error' ? 'error' : 'success',
            event_key: `clearProjectQueue:${responseCaseId}:${responseStatus}:${removedCount}`,
          });

          if (!normalizedServerMessage) {
            return;
          }

          setServerMessages(prev => {
            const filteredMessages = prev.filter(
              existingMessage => existingMessage.dedupeKey !== normalizedServerMessage.dedupeKey
            );
            const updatedMessages = [normalizedServerMessage, ...filteredMessages];
            if (updatedMessages.length > MAX_SERVER_MESSAGES) {
              return updatedMessages.slice(0, MAX_SERVER_MESSAGES);
            }

            return updatedMessages;
          });
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    });
    return unsubscribe;
  }, [MAX_NOTIFICATIONS, MAX_SERVER_MESSAGES, currentCaseId, subscribeToMessages, t]);

  const handleOpenNotifications = (event) => {
    setNotificationsAnchorEl(event.currentTarget);
    setHasUnreadNotifications(false);
  };

  const handleCloseNotifications = () => {
    setNotificationsAnchorEl(null);
  };

  const handleCloseServerMessage = (messageId) => (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setServerMessages(prev => prev.filter(msg => msg.id !== messageId));
  };
  const handleNotificationClick = (caseId) => {
    handleCloseNotifications();
    navigate(`/case/${caseId}`);
  };

  const handleClearNotifications = () => {
    setNotifications([]);
    setHasUnreadNotifications(false);
    handleCloseNotifications();
  };

  const handleOpenFileProcessingActions = (event) => {
    if (fileProcessingBadgeCount <= 0) {
      return;
    }

    setFileProcessingActionsAnchorEl(event.currentTarget);

    if (canManageProject) {
      sendMessage({
        command: 'get_active_tasks_info',
        data: {
          max_queue_files: 0,
          max_progress_files: 20,
        },
      });
    }
  };

  const handleCloseFileProcessingActions = () => {
    setFileProcessingActionsAnchorEl(null);
  };

  const handleClearProjectQueue = () => {
    if (!currentCaseId || !canManageProject || !isConnected || isClearQueuePending) {
      return;
    }

    setPostProcessingActionsAnchorEl(null);
    setPendingClearQueueCaseId(currentCaseId);
    sendMessage({
      command: 'clearProjectQueue',
      caseid: currentCaseId,
    });
  };

  const handleOpenPostProcessingActions = (event) => {
    if (!isPostProcessingVisible) {
      return;
    }

    setPostProcessingActionsAnchorEl(event.currentTarget);
  };

  const handleClosePostProcessingActions = () => {
    setPostProcessingActionsAnchorEl(null);
  };

  useEffect(() => {
    if (fileProcessingBadgeCount <= 0 || !isConnected) {
      setFileProcessingActionsAnchorEl(null);
    }
  }, [fileProcessingBadgeCount, isConnected]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return undefined;
    }

    document.documentElement.style.setProperty('--statusbar-badge-row-width', `${statusBarBadgeRowWidth}px`);
    return () => {
      document.documentElement.style.removeProperty('--statusbar-badge-row-width');
    };
  }, [statusBarBadgeRowWidth]);

  // Helper function to get the appropriate severity and colors for server messages
  const getMessageSeverityAndColors = (type) => {
    switch (type) {
      case 'error':
        return {
          severity: 'error',
          bgColor: '#ffebee',
          borderColor: '#f44336',
          textColor: '#d32f2f'
        };
      case 'warning':
        return {
          severity: 'warning',
          bgColor: '#fff8e1',
          borderColor: '#ff9800',
          textColor: '#f57c00'
        };
      case 'success':
        return {
          severity: 'success',
          bgColor: '#e8f5e8',
          borderColor: '#4caf50',
          textColor: '#2e7d32'
        };
      default: // 'info'
        return {
          severity: 'info',
          bgColor: lightPurple,
          borderColor: primaryColor,
          textColor: primaryColor
        };
    }
  }; return (
    <>
      {(fileProcessingBadgeCount > 0 || isPostProcessingVisible || notifications.length > 0) && (
        <Box sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 1200,
          animation: `${floatAnimation} 4s ease-in-out infinite`,
        }}
        >
          <Stack direction="row" spacing={0.5} alignItems="center">
            {fileProcessingBadgeCount > 0 && (
              <Tooltip
                title={fileProcessingSummaryContent}
                placement="bottom-start"
                arrow
                sx={{
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: 'rgba(233, 30, 99, 0.95)',
                    color: 'white',
                    fontSize: '0.75rem',
                    borderRadius: '8px',
                    maxWidth: '300px',
                  },
                  '& .MuiTooltip-arrow': {
                    color: 'rgba(233, 30, 99, 0.95)',
                  }
                }}
              >
                <Box
                  sx={{ cursor: 'pointer' }}
                  onClick={handleOpenFileProcessingActions}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (!fileProcessingBadgeCount) {
                      return;
                    }

                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleOpenFileProcessingActions(event);
                    }
                  }}
                  aria-haspopup="menu"
                  aria-expanded={isFileProcessingActionsOpen ? 'true' : undefined}
                >
                  <Badge
                    badgeContent={fileProcessingBadgeCount}
                    max={BADGE_MAX_COUNT}
                    overlap="circular"
                    invisible={fileProcessingBadgeCount <= 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#e91e63',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                        border: '2px solid rgba(255, 255, 255, 0.96)',
                        boxShadow: '0 2px 6px rgba(233, 30, 99, 0.28)',
                      }
                    }}
                  >
                    <Box sx={{
                      width: 34,
                      height: 34,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.96)',
                      color: '#e91e63',
                      border: '1px solid rgba(233, 30, 99, 0.24)',
                      boxShadow: '0 4px 16px rgba(233, 30, 99, 0.18)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.25s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#fff5f8',
                        boxShadow: '0 6px 20px rgba(233, 30, 99, 0.26)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                    >
                      <CircularProgress
                        size={16}
                        sx={{
                          color: '#e91e63',
                          '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round',
                          }
                        }}
                      />
                    </Box>
                  </Badge>
                </Box>
              </Tooltip>
            )}

            {isPostProcessingVisible && (
              <Tooltip
                title={postProcessingSummaryContent}
                placement="bottom-start"
                arrow
                sx={{
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: 'rgba(0, 121, 107, 0.96)',
                    color: 'white',
                    fontSize: '0.75rem',
                    borderRadius: '8px',
                    maxWidth: '320px',
                  },
                '& .MuiTooltip-arrow': {
                  color: 'rgba(0, 121, 107, 0.96)',
                }
              }}
              >
                <Box
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={handleOpenPostProcessingActions}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (!isPostProcessingVisible) {
                      return;
                    }
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleOpenPostProcessingActions(event);
                    }
                  }}
                  aria-haspopup="menu"
                  aria-expanded={isPostProcessingActionsOpen ? 'true' : undefined}
                >
                  <Badge
                    badgeContent={hasWaitingPostProcessingQueue ? currentCasePostProcessingWaitingCount : 0}
                    max={BADGE_MAX_COUNT}
                    overlap="circular"
                    invisible={!hasWaitingPostProcessingQueue}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: '#00695c',
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                        border: '2px solid rgba(255, 255, 255, 0.96)',
                        boxShadow: '0 2px 6px rgba(0, 105, 92, 0.28)',
                      }
                    }}
                  >
                    <Box sx={{
                      width: 34,
                      height: 34,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255, 255, 255, 0.96)',
                      color: '#00695c',
                      border: '1px solid rgba(0, 105, 92, 0.24)',
                      boxShadow: '0 4px 16px rgba(0, 105, 92, 0.18)',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.25s ease-in-out',
                      '&:hover': {
                        backgroundColor: '#ecfdf8',
                        boxShadow: '0 6px 20px rgba(0, 105, 92, 0.26)',
                        transform: 'translateY(-1px)',
                      }
                    }}
                    >
                      <CircularProgress
                        size={16}
                        variant={isPostProcessingAnimating ? 'indeterminate' : 'determinate'}
                        value={100}
                        sx={{
                          color: '#00695c',
                          '& .MuiCircularProgress-circle': {
                            strokeLinecap: 'round',
                          }
                        }}
                      />
                    </Box>
                  </Badge>
                </Box>
              </Tooltip>
            )}

            {notifications.length > 0 && (
              <Tooltip
                title={t(
                  'status_bar.file_updates_title',
                  'Recent file updates'
                )}
                placement="bottom-start"
                arrow
                sx={{
                  '& .MuiTooltip-tooltip': {
                    backgroundColor: 'rgba(87, 87, 176, 0.96)',
                    color: 'white',
                    fontSize: '0.75rem',
                    borderRadius: '8px',
                    maxWidth: '280px',
                  },
                  '& .MuiTooltip-arrow': {
                    color: 'rgba(87, 87, 176, 0.96)',
                  }
                }}
              >
                <Box sx={{ cursor: 'pointer' }}>
                  <Badge
                    badgeContent={notifications.length}
                    max={BADGE_MAX_COUNT}
                    overlap="circular"
                    invisible={notifications.length <= 0}
                    sx={{
                      '& .MuiBadge-badge': {
                        backgroundColor: hasUnreadNotifications ? '#d32f2f' : primaryColor,
                        color: 'white',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 4px',
                        border: '2px solid rgba(255, 255, 255, 0.96)',
                        boxShadow: `0 2px 6px ${hasUnreadNotifications ? 'rgba(211, 47, 47, 0.28)' : 'rgba(87, 87, 176, 0.28)'}`,
                      }
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={handleOpenNotifications}
                      aria-label={t('status_bar.updates_button_aria', 'Open file update status')}
                      sx={{
                        width: 34,
                        height: 34,
                        color: hasUnreadNotifications ? '#d32f2f' : primaryColor,
                        backgroundColor: 'rgba(255, 255, 255, 0.96)',
                        border: `1px solid ${hasUnreadNotifications ? 'rgba(211, 47, 47, 0.24)' : 'rgba(87, 87, 176, 0.24)'}`,
                        boxShadow: `0 4px 16px ${hasUnreadNotifications ? 'rgba(211, 47, 47, 0.18)' : 'rgba(87, 87, 176, 0.18)'}`,
                        backdropFilter: 'blur(10px)',
                        transition: 'all 0.25s ease-in-out',
                        '&:hover': {
                          backgroundColor: lightPurple,
                          boxShadow: `0 6px 20px ${hasUnreadNotifications ? 'rgba(211, 47, 47, 0.24)' : 'rgba(87, 87, 176, 0.24)'}`,
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <NotificationsOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Badge>
                </Box>
              </Tooltip>
            )}
          </Stack>
        </Box>
      )}

      <Popover
        open={isPostProcessingActionsOpen && isPostProcessingVisible}
        anchorEl={postProcessingActionsAnchorEl}
        onClose={handleClosePostProcessingActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 240,
            borderRadius: 3,
            border: '1px solid rgba(0, 105, 92, 0.18)',
            boxShadow: '0 16px 38px rgba(20, 24, 74, 0.18)',
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          {postProcessingSummaryContent}
        </Box>
        <Divider />
        <List dense disablePadding>
          {canClearPostProcessingQueue ? (
            <ListItemButton
              onClick={handleClearProjectQueue}
              disabled={isClearQueuePending || !isConnected}
              sx={{
                px: 2,
                py: 1.25,
                alignItems: 'center',
                gap: 1.5,
                color: '#c62828',
                '&:hover': {
                  backgroundColor: '#ffebee',
                },
                '&.Mui-disabled': {
                  color: 'rgba(198, 40, 40, 0.35)',
                },
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
              <ListItemText
                primary={t(
                  'status_bar.clear_queue_option',
                  'Clear queue'
                )}
                secondary={t(
                  'status_bar.clear_queue_option_description',
                  'Remove waiting post-processing jobs for this project'
                )}
                primaryTypographyProps={{
                  sx: { fontWeight: 800, color: 'inherit' }
                }}
                secondaryTypographyProps={{
                  sx: { color: 'inherit', opacity: 0.8 }
                }}
              />
            </ListItemButton>
          ) : (
            <ListItemButton
              disabled
              sx={{
                px: 2,
                py: 1.25,
                alignItems: 'center',
                gap: 1.5,
                color: 'rgba(0, 105, 92, 0.72)',
              }}
            >
              <DeleteOutlineIcon fontSize="small" />
              <ListItemText
                primary={t(
                  'status_bar.clear_queue_unavailable',
                  'Nothing to clear'
                )}
                secondary={t(
                  'status_bar.clear_queue_unavailable_description',
                  'No waiting post-processing jobs for this project'
                )}
                primaryTypographyProps={{
                  sx: { fontWeight: 800, color: 'inherit' }
                }}
                secondaryTypographyProps={{
                  sx: { color: 'inherit', opacity: 0.8 }
                }}
              />
            </ListItemButton>
          )}
        </List>
      </Popover>

      <Popover
        open={isFileProcessingActionsOpen}
        anchorEl={fileProcessingActionsAnchorEl}
        onClose={handleCloseFileProcessingActions}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            minWidth: 300,
            maxWidth: { xs: 'calc(100vw - 32px)', sm: 460 },
            borderRadius: 3,
            border: '1px solid rgba(233, 30, 99, 0.18)',
            boxShadow: '0 16px 38px rgba(20, 24, 74, 0.18)',
            overflow: 'hidden',
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          {fileProcessingSummaryContent}
        </Box>
        <Divider />
        <List dense disablePadding>
          {!canManageProject ? (
            <ListItem sx={{ px: 2, py: 1.5 }}>
              <ListItemText
                primary={t(
                  'status_bar.file_processing_restricted',
                  'Detailed event processing information is restricted'
                )}
                secondary={t(
                  'status_bar.file_processing_restricted_description',
                  'Project managers can open the detailed active tasks feed.'
                )}
                primaryTypographyProps={{
                  sx: { fontWeight: 800, color: '#24243a' }
                }}
                secondaryTypographyProps={{
                  sx: { color: '#6b7280' }
                }}
              />
            </ListItem>
          ) : activeTasksInfo === null ? (
            <ListItem sx={{ px: 2, py: 1.5 }}>
              <ListItemText
                primary={t(
                  'status_bar.file_processing_loading',
                  'Loading active task details...'
                )}
                secondary={t(
                  'status_bar.file_processing_loading_description',
                  'Fetching the latest list of files currently in progress.'
                )}
                primaryTypographyProps={{
                  sx: { fontWeight: 800, color: '#24243a' }
                }}
                secondaryTypographyProps={{
                  sx: { color: '#6b7280' }
                }}
              />
            </ListItem>
          ) : fileProcessingDetails?.error ? (
            <ListItem sx={{ px: 2, py: 1.5 }}>
              <ListItemText
                primary={t(
                  'status_bar.file_processing_error',
                  'Unable to load detailed processing information'
                )}
                secondary={String(fileProcessingDetails.error)}
                primaryTypographyProps={{
                  sx: { fontWeight: 800, color: '#c62828' }
                }}
                secondaryTypographyProps={{
                  sx: { color: '#8e2b2b' }
                }}
              />
            </ListItem>
          ) : fileProcessingItems.length > 0 ? (
            <>
              {fileProcessingItems.map((file, index) => (
                <ListItem
                  key={`${file?.path || file?.filename || 'file'}-${index}`}
                  alignItems="flex-start"
                  sx={{
                    px: 2,
                    py: 1.25,
                    borderBottom: index < fileProcessingItems.length - 1
                      ? '1px solid rgba(233, 30, 99, 0.08)'
                      : 'none',
                    '&:hover': {
                      backgroundColor: '#fff5f8',
                    },
                  }}
                >
                  <ListItemText
                    primary={file?.filename || t(
                      'status_bar.file_processing_unknown_file',
                      'Unknown file'
                    )}
                    secondary={(
                      <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: '#6b7280' }}
                        >
                          {t(
                            'status_bar.file_processing_path',
                            'Path: {{path}}',
                            { path: formatCompactPath(file?.path) || '-' }
                          )}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: '#6b7280' }}
                        >
                          {t(
                            'status_bar.file_processing_projects',
                            'Project IDs: {{ids}}',
                            {
                              ids: Array.isArray(file?.case_ids) && file.case_ids.length > 0
                                ? file.case_ids.join(', ')
                                : '-'
                            }
                          )}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#9ca3af',
                            fontWeight: 600,
                            letterSpacing: '0.02em',
                          }}
                        >
                          {t(
                            'status_bar.file_processing_started',
                            'Started: {{time}}',
                            {
                              time: formatNotificationTimestamp(file?.timestamp, notificationLocale)
                            }
                          )}
                        </Typography>
                      </Stack>
                    )}
                    primaryTypographyProps={{
                      noWrap: true,
                      sx: { fontWeight: 700, color: '#24243a' }
                    }}
                  />
                </ListItem>
              ))}
              {fileProcessingIsTruncated && (
                <ListItem sx={{ px: 2, py: 1.25 }}>
                  <ListItemText
                    primary={t(
                      'status_bar.file_processing_truncated',
                      '... and {{count}} more event(s) are not shown.',
                      {
                        count: Math.max(
                          fileProcessingProgressTotal - fileProcessingProgressReturned,
                          0
                        )
                      }
                    )}
                    primaryTypographyProps={{
                      sx: {
                        color: '#6b7280',
                        fontWeight: 600,
                      }
                    }}
                  />
                </ListItem>
              )}
            </>
          ) : (
            <ListItem sx={{ px: 2, py: 1.5 }}>
              <ListItemText
                primary={t(
                  'status_bar.file_processing_empty',
                  'No detailed processing items available'
                )}
                secondary={t(
                  'status_bar.file_processing_empty_description',
                  'The processing queue is empty or the active tasks feed has not returned any files yet.'
                )}
                primaryTypographyProps={{
                  sx: { fontWeight: 800, color: '#24243a' }
                }}
                secondaryTypographyProps={{
                  sx: { color: '#6b7280' }
                }}
              />
            </ListItem>
          )}
        </List>
      </Popover>

      <Popover
        open={isNotificationsOpen}
        anchorEl={notificationsAnchorEl}
        onClose={handleCloseNotifications}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxWidth: 360,
            borderRadius: 3,
            border: `1px solid rgba(87, 87, 176, 0.18)`,
            boxShadow: '0 16px 38px rgba(20, 24, 74, 0.18)',
            overflow: 'hidden',
          }
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: primaryColor }}>
            {t('status_bar.file_updates_title', 'Recent file updates')}
          </Typography>
          <Button size="small" onClick={handleClearNotifications} sx={{ textTransform: 'none', fontWeight: 700 }}>
            {t('status_bar.clear_updates', 'Clear')}
          </Button>
        </Stack>
        <Divider />
        <List dense disablePadding>
          {notifications.map((notification) => (
            <ListItemButton
              key={notification.id}
              onClick={() => handleNotificationClick(notification.caseid)}
              sx={{
                px: 2,
                py: 1.25,
                alignItems: 'flex-start',
                '&:hover': {
                  backgroundColor: lightPurple,
                },
              }}
            >
              <ListItemText
                primary={notification.filename}
                secondary={
                  <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: '#6b7280' }}
                    >
                      {t(
                        'status_bar.project_update',
                        'Project {{title}} has an update',
                        { title: notification.title }
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#9ca3af',
                        fontWeight: 600,
                        letterSpacing: '0.02em',
                      }}
                    >
                      {formatNotificationTimestamp(notification.timestamp, notificationLocale)}
                    </Typography>
                  </Stack>
                }
                primaryTypographyProps={{
                  noWrap: true,
                  sx: { fontWeight: 700, color: '#24243a' }
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Popover>

      {/* Server Messages - limited to MAX_SERVER_MESSAGES */}
      {serverMessages.map((serverMessage, index) => {
        const { severity, bgColor, borderColor, textColor } = getMessageSeverityAndColors(serverMessage.type);
        return (
          <Snackbar
            key={serverMessage.id}
            open={true}
            autoHideDuration={serverMessage.type === 'error' ? 6000 : 4000} // Error messages stay longer
            onClose={handleCloseServerMessage(serverMessage.id)}
            TransitionComponent={Slide}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            style={{
              marginBottom: index * 60,
              zIndex: 1400 // Higher than notifications to appear on top
            }}
          >
            <Alert
              onClose={handleCloseServerMessage(serverMessage.id)}
              severity={severity}
              sx={{
                width: '100%',
                backgroundColor: bgColor,
                color: textColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '12px',
                boxShadow: `0 4px 12px ${borderColor}25`,
                '& .MuiAlert-icon': {
                  color: textColor
                },
                '& .MuiAlert-message': {
                  fontWeight: 500
                }
              }}
            >
              {serverMessage.message}
            </Alert>
          </Snackbar>
        );
      })}
    </>
  );
};

export default StatusBar;
